import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, HelpCircle, Shield, Loader2, ArrowRight, Building2, MapPin, Package, Award, Layers, FileText, Info, Check, X, Scale } from 'lucide-react';
import api from '../api';
import { useToast } from '../hooks/use-toast';

const VERIF_LABELS = {
  declared: 'Déclaré par le manufacturier',
  document_received: 'Document reçu',
  yuville_verified: 'Vérification Yuville effectuée',
  to_verify_audit: 'À vérifier lors de l\'audit',
};
const VERIF_STYLES = {
  declared: 'bg-gray-100 text-gray-700 border-gray-300',
  document_received: 'bg-blue-50 text-blue-700 border-blue-300',
  yuville_verified: 'bg-green-50 text-green-700 border-green-500',
  to_verify_audit: 'bg-amber-50 text-amber-800 border-amber-400',
};

// Fields shown in the summary vs. details, and per-project-type highlighting.
const CASE_A_HIGHLIGHT = new Set(['modes', 'complianceLevel', 'productionCapacity', 'moq', 'certifications', 'productionDelay', 'price']);
const CASE_B_HIGHLIGHT = new Set(['certifications', 'complianceLevel', 'price', 'moq', 'exportExperience', 'commercialTerms', 'productionDelay']);

const LABELS = {
  city: 'Ville', province: 'Province', website: 'Site Web',
  yearsActive: "Années d'activité", specialization: 'Spécialisation',
  products: 'Produits fabriqués', productionCapacity: 'Capacité de production',
  complianceLevel: 'Niveau de conformité au cahier des charges',
  certifications: 'Certifications',
  exportExperience: "Expérience à l'exportation",
  moq: 'MOQ', price: 'Prix ou fourchette indicative',
  productionDelay: 'Délais de production',
  modes: 'OEM / ODM / Marque privée',
  commercialTerms: 'Conditions commerciales',
  strengths: 'Principaux avantages',
  pointsToVerify: 'Points à vérifier',
  yuvilleAssessment: 'Appréciation de Yuville',
};

const ALL_FIELDS = ['specialization', 'products', 'productionCapacity', 'complianceLevel', 'certifications', 'exportExperience', 'moq', 'price', 'productionDelay', 'modes', 'commercialTerms'];

export default function DossierPage() {
  const { rfqId } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get('token') || '';
  const { toast } = useToast();
  const [state, setState] = useState({ loading: true, error: '', data: null });
  const [compare, setCompare] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [validating, setValidating] = useState(false);

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const res = await api.get(`/client/rfq/${rfqId}?token=${encodeURIComponent(token)}`);
      setState({ loading: false, error: '', data: res.data });
    } catch (e) {
      setState({ loading: false, error: e?.response?.data?.detail || 'Lien invalide ou expiré.', data: null });
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [rfqId, token]);

  const rfq = state.data?.rfq;
  const manufacturers = state.data?.manufacturers || [];
  const isCaseA = rfq?.project?.type === 'custom' || rfq?.project?.type === 'manufacturer';
  const highlight = isCaseA ? CASE_A_HIGHLIGHT : CASE_B_HIGHLIGHT;
  const validated = !!rfq?.shortlistValidatedAt;

  const vote = async (mid, decision) => {
    try {
      await api.post(`/client/rfq/${rfqId}/vote?token=${encodeURIComponent(token)}`, { manufacturer_id: mid, decision });
      load();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Impossible', duration: 8000 });
    }
  };

  const validateSelection = async () => {
    setValidating(true);
    try {
      const res = await api.post(`/client/rfq/${rfqId}/validate?token=${encodeURIComponent(token)}`);
      toast({ title: 'Sélection validée', description: `${res.data.count} manufacturier(s) prêt(s) pour l'étape 03.`, duration: 15000 });
      load();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Validation impossible', duration: 10000 });
    } finally { setValidating(false); }
  };

  const toggleCompare = (id) => {
    setCompare((c) => c.includes(id) ? c.filter((x) => x !== id) : c.length >= 3 ? c : [...c, id]);
  };

  const compareList = useMemo(() => manufacturers.filter((m) => compare.includes(m.id)), [manufacturers, compare]);
  const toDeepenCount = manufacturers.filter((m) => m.clientDecision === 'to_deepen').length;

  if (state.loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50"><Loader2 className="w-8 h-8 animate-spin text-brand-yellow" /></div>;
  }
  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6">
        <div className="max-w-md text-center bg-white p-8 rounded-xl shadow-md border border-gray-200">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-brand-navy mb-2">Accès impossible</h1>
          <p className="text-gray-600 text-sm mb-6">{state.error}</p>
          <Link to="/" className="inline-flex items-center gap-2 text-brand-navy hover:text-brand-yellow text-sm font-semibold">← Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-brand-navy text-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8">
          <Link to="/" className="text-brand-yellow text-xs font-bold tracking-widest uppercase">← Yuville International</Link>
          <div className="inline-flex items-center gap-2 mt-3 mb-2 px-3 py-1 rounded-full bg-brand-yellow/20 border border-brand-yellow text-brand-yellow text-[11px] font-bold tracking-widest uppercase">02 · Short-list</div>
          <h1 data-testid="dossier-title" className="text-3xl md:text-4xl font-bold leading-tight">Votre short-list de manufacturiers</h1>
          <p className="mt-3 text-white/80 max-w-3xl leading-relaxed">
            Sur la base de votre cahier des charges, notre équipe a recherché, qualifié et présélectionné les manufacturiers ci-dessous. Consultez les détails, comparez, puis indiquez ceux que vous souhaitez <b className="text-brand-yellow">approfondir</b> en vue de l'étape suivante (audit sur place).
          </p>
        </div>
      </header>

      {/* Brief recap */}
      <div className="max-w-6xl mx-auto px-6 lg:px-10 -mt-4">
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-5 grid md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-brand-yellow font-bold">Client</div>
            <div className="text-brand-navy font-semibold">{rfq?.contact?.name}</div>
            {rfq?.contact?.company && <div className="text-gray-500 text-xs">{rfq.contact.company}</div>}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-brand-yellow font-bold">Projet</div>
            <div className="text-brand-navy">{rfq?.project?.type} {rfq?.project?.typeOther && `(${rfq.project.typeOther})`}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-brand-yellow font-bold">Produit</div>
            <div className="text-brand-navy">{rfq?.need?.product || '—'}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-brand-yellow font-bold">Manufacturiers</div>
            <div className="text-brand-navy font-bold text-lg">{manufacturers.length}</div>
          </div>
        </div>
      </div>

      {/* Verification legend */}
      <div className="max-w-6xl mx-auto px-6 lg:px-10 mt-6">
        <div className="bg-brand-yellow/10 border border-brand-yellow/40 rounded-lg p-4 text-xs">
          <div className="flex items-center gap-2 mb-2 font-bold text-brand-navy"><Shield className="w-4 h-4 text-brand-yellow" /> Comment lire les statuts de vérification</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(VERIF_LABELS).map(([k, v]) => (
              <span key={k} className={`px-2 py-1 rounded-full border ${VERIF_STYLES[k]} text-[11px]`}>{v}</span>
            ))}
          </div>
          <p className="mt-2 text-gray-600 leading-relaxed">Une information « déclarée » n'a pas encore été vérifiée par Yuville. Les vérifications indépendantes seront finalisées lors de l'étape 03 — audit sur place.</p>
        </div>
      </div>

      {/* Manufacturers grid */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 mt-8 space-y-5">
        {manufacturers.length === 0 && (
          <div className="bg-white p-10 rounded-xl border border-gray-100 text-center">
            <Package className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">La short-list est en cours de préparation. Vous recevrez une notification dès qu'elle sera prête.</p>
          </div>
        )}

        {manufacturers.map((m) => (
          <ManufacturerCard key={m.id} m={m} highlight={highlight} onVote={vote} disabled={validated} isCompareChecked={compare.includes(m.id)} onToggleCompare={() => toggleCompare(m.id)} />
        ))}
      </section>

      {/* Sticky action bar */}
      {!validated && manufacturers.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 shadow-[0_-4px_16px_rgba(0,0,0,0.05)] z-40">
          <div className="max-w-6xl mx-auto px-6 lg:px-10 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-sm text-brand-navy">
              <span className="font-bold text-lg text-brand-yellow">{toDeepenCount}</span> à approfondir · <span className="text-gray-500">{compare.length} sélectionné(s) pour comparaison (max 3)</span>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              {compare.length >= 2 && (
                <button onClick={() => setShowCompare(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-brand-navy text-brand-navy text-sm font-semibold hover:bg-brand-navy hover:text-white transition">
                  <Scale className="w-4 h-4" /> Comparer ({compare.length})
                </button>
              )}
              <button onClick={validateSelection} disabled={validating || toDeepenCount === 0} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-yellow text-brand-navy text-sm font-bold hover:bg-yellow-400 transition disabled:opacity-50 disabled:cursor-not-allowed">
                {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Valider ma sélection
              </button>
            </div>
          </div>
        </div>
      )}

      {validated && (
        <div className="max-w-6xl mx-auto px-6 lg:px-10 mt-8">
          <div className="bg-green-50 border-2 border-green-400 rounded-xl p-6 text-center">
            <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-brand-navy mb-2">Sélection validée</h2>
            <p className="text-gray-700 max-w-2xl mx-auto">Merci ! Les manufacturiers que vous avez choisis <b>« à approfondir »</b> passeront à l'étape suivante : <b>03 — Audit sur place</b>. Nous vous recontacterons rapidement pour organiser les vérifications.</p>
          </div>
        </div>
      )}

      {/* Comparison overlay */}
      {showCompare && compareList.length >= 2 && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowCompare(false)}>
          <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-brand-navy text-white px-6 py-4 flex items-center justify-between">
              <h2 className="font-bold text-lg">Comparaison — {compareList.length} manufacturiers</h2>
              <button onClick={() => setShowCompare(false)} className="text-white/80 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 overflow-x-auto">
              <table className="w-full text-sm min-w-[600px]">
                <thead>
                  <tr className="text-left border-b-2 border-gray-200">
                    <th className="py-2 pr-4 font-bold text-brand-navy text-xs uppercase tracking-wider">Critère</th>
                    {compareList.map((m) => <th key={m.id} className="py-2 px-3 font-bold text-brand-navy">{m.name || m.anonymousCode || 'Manufacturier'}<div className="text-[11px] font-normal text-gray-500">{m.city || m.anonymousCode || ''}</div></th>)}
                  </tr>
                </thead>
                <tbody>
                  {ALL_FIELDS.map((f) => (
                    <tr key={f} className="border-b border-gray-100 align-top">
                      <td className={`py-3 pr-4 text-xs ${highlight.has(f) ? 'font-bold text-brand-yellow' : 'text-gray-600'}`}>{LABELS[f]}</td>
                      {compareList.map((m) => (
                        <td key={m.id} className="py-3 px-3 text-brand-navy">
                          {m[f] || '—'}
                          {m.verifStatus?.[f] && (
                            <div className={`inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full border ${VERIF_STYLES[m.verifStatus[f]]}`}>{VERIF_LABELS[m.verifStatus[f]]}</div>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function ManufacturerCard({ m, highlight, onVote, disabled, isCompareChecked, onToggleCompare }) {
  const [expanded, setExpanded] = useState(false);
  const dec = m.clientDecision || '';
  const decBadge = {
    to_deepen: { label: 'À approfondir', style: 'bg-green-500 text-white' },
    need_more_info: { label: 'Plus d\'info demandée', style: 'bg-blue-500 text-white' },
    dismiss: { label: 'Écarté', style: 'bg-red-500 text-white' },
  }[dec];

  const summary = ['specialization', 'productionCapacity', 'certifications', 'moq', 'price', 'productionDelay'];
  const detailed = ALL_FIELDS.filter((f) => !summary.includes(f));

  return (
    <article data-testid={`manufacturer-card-${m.id}`} className={`bg-white rounded-xl border-2 transition-all overflow-hidden ${dec === 'to_deepen' ? 'border-green-400 shadow-lg' : dec === 'dismiss' ? 'border-red-200 opacity-70' : 'border-gray-200 hover:border-brand-yellow'}`}>
      <div className="p-6">
        <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="text-xl md:text-2xl font-bold text-brand-navy">
                {m.name || m.anonymousCode || 'Manufacturier'}
              </h3>
              {!m.identityRevealed && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-800 text-white font-bold uppercase tracking-wider" title="Identité révélée après acceptation du mandat">Identité masquée</span>
              )}
              {decBadge && <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${decBadge.style}`}>{decBadge.label}</span>}
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
              {m.anonymousCode && <span className="font-mono text-brand-navy/70">{m.anonymousCode}</span>}
              {(m.city || m.province) && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{m.city}{m.province ? `, ${m.province}` : ''}</span>}
              {m.yearsActive && <span>{m.yearsActive}</span>}
              {m.website && <a href={m.website} target="_blank" rel="noreferrer" className="text-brand-navy hover:text-brand-yellow underline">{m.website.replace(/^https?:\/\//, '')}</a>}
            </div>
          </div>
          <label className="shrink-0 inline-flex items-center gap-2 text-xs font-semibold text-brand-navy cursor-pointer">
            <input type="checkbox" checked={isCompareChecked} onChange={onToggleCompare} className="w-4 h-4 accent-brand-yellow" /> Comparer
          </label>
        </div>

        {/* Summary grid */}
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          {summary.map((f) => m[f] && (
            <Field key={f} label={LABELS[f]} value={m[f]} highlight={highlight.has(f)} verif={m.verifStatus?.[f]} />
          ))}
        </div>

        {m.yuvilleAssessment && (
          <div className="mb-4 p-4 rounded-lg bg-brand-navy text-white">
            <div className="text-[10px] uppercase tracking-widest text-brand-yellow font-bold mb-1">Appréciation de Yuville</div>
            <p className="text-sm leading-relaxed">{m.yuvilleAssessment}</p>
          </div>
        )}

        {expanded && (
          <>
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              {detailed.map((f) => m[f] && (
                <Field key={f} label={LABELS[f]} value={m[f]} highlight={highlight.has(f)} verif={m.verifStatus?.[f]} />
              ))}
            </div>
            {m.strengths && (
              <div className="mb-3 p-3 bg-green-50 border-l-4 border-green-400 rounded">
                <div className="text-[10px] uppercase font-bold text-green-700 tracking-widest">Principaux avantages</div>
                <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{m.strengths}</p>
              </div>
            )}
            {m.pointsToVerify && (
              <div className="mb-3 p-3 bg-amber-50 border-l-4 border-amber-400 rounded">
                <div className="text-[10px] uppercase font-bold text-amber-700 tracking-widest">Points à vérifier</div>
                <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{m.pointsToVerify}</p>
              </div>
            )}
          </>
        )}

        <button onClick={() => setExpanded(!expanded)} className="text-xs font-semibold text-brand-navy hover:text-brand-yellow inline-flex items-center gap-1">
          {expanded ? '− Masquer les détails' : '+ Voir tous les détails'}
        </button>
      </div>

      {/* Actions bar */}
      <div className="bg-gray-50 border-t border-gray-200 px-6 py-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-gray-500">Votre décision :</div>
        <div className="flex gap-2 flex-wrap">
          <VoteBtn active={dec === 'to_deepen'} onClick={() => onVote(m.id, dec === 'to_deepen' ? '' : 'to_deepen')} icon={CheckCircle2} label="À approfondir" activeStyle="bg-green-500 text-white border-green-500" disabled={disabled} testid={`vote-deepen-${m.id}`} />
          <VoteBtn active={dec === 'need_more_info'} onClick={() => onVote(m.id, dec === 'need_more_info' ? '' : 'need_more_info')} icon={HelpCircle} label="Demander plus d'info" activeStyle="bg-blue-500 text-white border-blue-500" disabled={disabled} />
          <VoteBtn active={dec === 'dismiss'} onClick={() => onVote(m.id, dec === 'dismiss' ? '' : 'dismiss')} icon={XCircle} label="Écarter" activeStyle="bg-red-500 text-white border-red-500" disabled={disabled} />
        </div>
      </div>
    </article>
  );
}

function Field({ label, value, highlight, verif }) {
  return (
    <div className={`p-3 rounded-lg border ${highlight ? 'bg-brand-yellow/5 border-brand-yellow/30' : 'bg-white border-gray-100'}`}>
      <div className={`text-[10px] uppercase font-bold tracking-widest mb-1 ${highlight ? 'text-brand-yellow' : 'text-gray-500'}`}>{label}</div>
      <div className="text-sm text-brand-navy whitespace-pre-wrap">{value}</div>
      {verif && <div className={`inline-block mt-2 text-[10px] px-2 py-0.5 rounded-full border ${VERIF_STYLES[verif]}`}>{VERIF_LABELS[verif]}</div>}
    </div>
  );
}

function VoteBtn({ active, onClick, icon: Icon, label, activeStyle, disabled, testid }) {
  return (
    <button data-testid={testid} disabled={disabled} onClick={onClick} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition ${active ? activeStyle : 'bg-white border-gray-300 text-brand-navy hover:border-brand-navy'} disabled:opacity-60 disabled:cursor-not-allowed`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}
